// Libs
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { format, startOfDay, subDays } from "date-fns";
import { es } from "date-fns/locale";
import { getOrderStatusDisplay } from "@/lib/statusConfig";
import { formatCurrency } from "@/lib/formatCurrency";

// Hooks
import { useHistoricalOrders } from "@/hooks/useHistoricalOrders";
import { useOrderFilters } from "@/hooks/useOrderFilters";
import { useOrder } from "@/hooks/useOrder";
import { useOrdersStore } from "@/pages/orders-new/use-orders-store";

// Components
import { Button } from "@/components/atoms/button";
import { Input } from "@/components/atoms/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/atoms/select";
import { Card, CardContent } from "@/components/atoms/card";
import { Skeleton } from "@/components/atoms/skeleton";
import LoadingSpinner from "@/components/orders/LoadingSpinner";
import OrdersTable from "@/components/orders/OrdersTable";
import StatusBadge from "@/components/orders/StatusBadge";
import { DatePicker } from "@/components/atoms/date-picker";
import {
  useHistoryFilterOptions,
  HistoryFilterOptions,
} from "@/hooks/useHistoryFilterOptions";
import { HistoryMultiSelect } from "@/components/orders/HistoryMultiSelect";
import {
  useOrderHistoryExport,
  exportRangeError,
} from "@/hooks/useOrderHistoryExport";
import { HistoryExportPanel } from "@/components/orders/HistoryExportPanel";
import { toastHandler } from "@/lib/ToastHandler";
// Realtime initialized in order-app; this page only listens for events

// Icons
import { RefreshCw, Search, Copy, Check, X, Download } from "lucide-react";

// Constants
const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_DATE_RANGE_DAYS = 30;

const STATUS_OPTIONS = [
  { value: "all", label: "Todos los estados" },
  { value: "pending", label: "Pendiente" },
  { value: "accepted", label: "Asignado" },
  { value: "sorted", label: "Surtida" },
  { value: "in_transit", label: "En tránsito" },
  { value: "on_site", label: "En sitio" },
  { value: "completed", label: "Completado" },
  { value: "cancelled", label: "Cancelado" },
];

// Types
interface FilterState {
  startDate: string;
  endDate: string;
  selectedBrand: string;
  selectedStatus: string;
  searchTerm: string;
  branchIds: string;
  driverIds: string;
}

interface OrderDisplayData {
  id: number;
  trackingId: string;
  branchName: string;
  driverName: string;
  brandName: string;
  status: string;
  statusDisplay: string;
  statusColor: string;
  isPaid: boolean;
  total: number;
  currency: string;
  createdAt: string;
  formattedDate: string;
}

export default function HistoricalOrders() {
  const navigate = useNavigate();
  const { items, loading, error, totalCount, hasMore, fetchOrders, refetch } =
    useHistoricalOrders();
  const [page, setPage] = useState(1);

  // State to track which order we want to navigate to
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const {
    order,
    loading: orderLoading,
    error: orderError,
  } = useOrder(selectedOrderId);

  // Use order filters hook for permission-based filtering (same as Orders.tsx)
  const { isLoadingUser, brandOptions } = useOrderFilters();
  const historyOptions = useHistoryFilterOptions();
  const historyExport = useOrderHistoryExport();

  // Effect to handle navigation once order is loaded
  useEffect(() => {
    if (selectedOrderId && order && !orderLoading) {
      useOrdersStore.setState({ orderDetail: order });
      navigate(`/orders/${selectedOrderId}`);
      setSelectedOrderId(null);
    } else if (selectedOrderId && orderError) {
      toastHandler({
        title: "Error",
        description: "Error al cargar la orden",
        variant: "destructive",
      });
      setSelectedOrderId(null);
    }
  }, [selectedOrderId, order, orderLoading, orderError, navigate]);

  // Show toast when realtime update affects visible list (today updates not expected here, but keep awareness)
  useEffect(() => {
    const onUpdate = (e: Event) => {
      const detail: any = (e as CustomEvent).detail;
      if (!detail) return;
      // For historical we just notify about updates
      toastHandler({
        title: "Actualización en tiempo real",
        description: `Orden ${detail?.orderId ?? ""} ${detail?.type?.toLowerCase?.() ?? "update"}.`,
        variant: "default",
      });
    };
    window.addEventListener(
      "orders-realtime:update",
      onUpdate as EventListener,
    );
    return () => {
      window.removeEventListener(
        "orders-realtime:update",
        onUpdate as EventListener,
      );
    };
  }, []);

  // Filters state
  const [filters, setFilters] = useState<FilterState>({
    startDate: "",
    endDate: "",
    selectedBrand: "all",
    selectedStatus: "all",
    searchTerm: "",
    branchIds: "",
    driverIds: "",
  });

  // Debounced search term - only updates after user stops typing for 300ms
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isSearchPending, setIsSearchPending] = useState(false);

  // Debounce the search term - immediate when clearing, 300ms when typing
  useEffect(() => {
    const delay = filters.searchTerm.trim() === "" ? 0 : 300;

    if (
      filters.searchTerm.trim() !== "" &&
      filters.searchTerm !== debouncedSearchTerm
    ) {
      setIsSearchPending(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(filters.searchTerm);
      setIsSearchPending(false);
    }, delay);

    return () => clearTimeout(timer);
  }, [filters.searchTerm, debouncedSearchTerm]);

  // Determine if data is ready to show (using same logic as Orders.tsx)
  // This ensures we don't try to fetch orders before user permissions are loaded
  const isDataReady = useMemo(() => {
    return !isLoadingUser;
  }, [isLoadingUser]);

  // Set default date range
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - DEFAULT_DATE_RANGE_DAYS);

    setFilters((prev) => ({
      ...prev,
      startDate: format(thirtyDaysAgo, "yyyy-MM-dd"),
      endDate: format(today, "yyyy-MM-dd"),
    }));
  }, []);

  // Fetch orders when filters change
  useEffect(() => {
    if (isDataReady) {
      handleSearch();
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.selectedBrand,
    filters.selectedStatus,
    filters.branchIds,
    filters.driverIds,
    debouncedSearchTerm,
    isDataReady, // Add this to trigger refetch when data becomes ready
  ]);

  const filteredItems = useMemo(() => {
    if (!isDataReady) return [];
    const filtered = [...items];
    // Sort chronologically - newest first
    return filtered.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [items, isDataReady]);

  // Memoized order display data
  const orderDisplayData = useMemo((): OrderDisplayData[] => {
    return filteredItems.map((order) => {
      const statusConfig = getOrderStatusDisplay(order.status);
      return {
        id: order.id,
        trackingId: order.tracking_id,
        branchName: order.branch?.name || "",
        driverName: order.driver?.name || "Sin asignar",
        brandName: order.branch?.brand?.name || "",
        status: order.status,
        statusDisplay: statusConfig.display,
        statusColor: statusConfig.color,
        isPaid: order.is_paid,
        total: order.subtotal + order.total_delivery_charge,
        currency: order.currency,
        createdAt: order.created_at,
        formattedDate: format(new Date(order.created_at), "dd/MM/yyyy", {
          locale: es,
        }),
      };
    });
  }, [filteredItems]);

  // Helper function to build consistent filter parameters
  const buildFilterParams = useCallback(
    (pageNum: number = 1) => {
      const params = {
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        branchIds: filters.branchIds || undefined,
        driverIds: filters.driverIds || undefined,
        brandId:
          filters.selectedBrand !== "all"
            ? parseInt(filters.selectedBrand)
            : undefined,
        status:
          filters.selectedStatus !== "all" ? filters.selectedStatus : undefined,
        searchTerm: debouncedSearchTerm || undefined,
        page: pageNum,
        pageSize: DEFAULT_PAGE_SIZE,
      };
      return params;
    },
    [filters, debouncedSearchTerm],
  );

  const handleSearch = useCallback(async () => {
    setPage(1);
    try {
      const params = buildFilterParams(1);
      await fetchOrders(params);
    } catch (error) {
      toastHandler({
        title: "Error",
        description: "Error al cargar las órdenes",
        variant: "destructive",
      });
    }
  }, [buildFilterParams, fetchOrders]);

  const exportError = exportRangeError(buildFilterParams());
  const exportDisabledReason = !isDataReady
    ? "Espera a que se carguen tus permisos."
    : isSearchPending || loading
      ? "Espera a que termine la búsqueda."
      : historyExport.busy
        ? "Ya hay una exportación en proceso."
        : exportError;

  const handleLoadMore = useCallback(async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    try {
      const params = buildFilterParams(nextPage);
      await fetchOrders(params);
    } catch (error) {
      toastHandler({
        title: "Error",
        description: "Error al cargar más órdenes",
        variant: "destructive",
      });
    }
  }, [page, buildFilterParams, fetchOrders]);

  const handleFilterChange = useCallback(
    (key: keyof FilterState, value: string) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
        ...(key === "selectedBrand" ? { branchIds: "" } : {}),
      }));
    },
    [],
  );

  const handleRefresh = useCallback(async () => {
    try {
      await refetch();
      toastHandler({
        title: "Actualizado",
        description: "Datos actualizados correctamente",
        variant: "default",
      });
    } catch (error) {
      toastHandler({
        title: "Error",
        description: "Error al actualizar los datos",
        variant: "destructive",
      });
    }
  }, [refetch]);

  // Handle order click - navigate to detail page
  const handleOrderClick = useCallback(async (orderId: string) => {
    setSelectedOrderId(orderId);
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.searchTerm.trim() !== "" ||
      filters.selectedBrand !== "all" ||
      filters.selectedStatus !== "all" ||
      filters.branchIds !== "" ||
      filters.driverIds !== "" ||
      filters.startDate !== "" ||
      filters.endDate !== ""
    );
  }, [filters]);

  // Handle clear all filters
  const handleClearAllFilters = useCallback(() => {
    // Reset filters to default values
    const defaultFilters: FilterState = {
      startDate: "",
      endDate: "",
      selectedBrand: "all",
      selectedStatus: "all",
      searchTerm: "",
      branchIds: "",
      driverIds: "",
    };

    setFilters(defaultFilters);
    setDebouncedSearchTerm("");

    setPage(1);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-2 sm:px-6 pt-4 pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              Historial de órdenes
            </h1>
            <p className="text-sm text-gray-600">
              Consulta y analiza órdenes de períodos anteriores
            </p>
          </div>
          <div className="flex-shrink-0 w-full sm:w-auto">
            <Button
              size="sm"
              variant="default"
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 w-full sm:w-auto bg-indigo-600 transition-colors duration-200 hover:bg-indigo-700 border-[#3F00FF] text-white"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""} hidden sm:block`}
              />
              Actualizar
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-6 py-4 sm:py-6">
        <HistoryExportPanel
          job={historyExport.job}
          error={historyExport.error}
          downloading={historyExport.downloading}
          onDownload={() => void historyExport.download()}
          onRetry={historyExport.retryStatus}
          onDismiss={historyExport.dismiss}
          canRetry={historyExport.canRetry}
        />
        {/* Filters Card */}
        <Card className="mb-6 bg-white">
          <CardContent>
            <FiltersSection
              filters={filters}
              options={historyOptions.options}
              loadingOptions={historyOptions.loading}
              optionsError={historyOptions.error}
              brands={brandOptions}
              loadingBrands={isLoadingUser || !isDataReady}
              isSearchPending={isSearchPending}
              onFilterChange={handleFilterChange}
              hasActiveFilters={hasActiveFilters}
              onClearAllFilters={handleClearAllFilters}
            />
          </CardContent>

          <div className="flex flex-col gap-4 border-y border-gray-100 bg-gray-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">
                Exportar historial
              </p>
              <p
                id="history-export-help"
                className={`mt-1 text-xs ${
                  exportDisabledReason ? "text-amber-700" : "text-gray-600"
                }`}
              >
                {exportDisabledReason ||
                  "Exporta todas las órdenes de los filtros seleccionados."}
              </p>
            </div>
            <div className="flex w-full items-center justify-between gap-4 sm:w-auto sm:justify-end">
              {!loading && !error && isDataReady && (
                <div
                  className="shrink-0 text-left sm:border-r sm:border-gray-200 sm:pr-4 sm:text-right"
                  role="status"
                  aria-atomic="true"
                >
                  <p className="text-xs text-gray-500">Órdenes filtradas</p>
                  <p className="text-base font-semibold tabular-nums leading-tight text-gray-900">
                    {totalCount.toLocaleString("es")}
                  </p>
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                className="shrink-0"
                disabled={Boolean(exportDisabledReason)}
                aria-describedby="history-export-help"
                onClick={() => void historyExport.start(buildFilterParams())}
              >
                <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                {historyExport.submitting
                  ? "Solicitando exportación..."
                  : "Exportar CSV"}
              </Button>
            </div>
          </div>

          {/* Orders List */}
          <CardContent className="p-4 sm:p-6 !pt-0">
            <HistoricalOrdersTable
              orders={orderDisplayData}
              loading={loading || !isDataReady}
              error={error}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              onOrderClick={handleOrderClick}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Helper Components
function HistoricalOrdersTable({
  orders,
  loading,
  error,
  hasMore,
  onLoadMore,
  onOrderClick,
}: {
  orders: OrderDisplayData[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  onOrderClick: (orderId: string) => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = useCallback((trackingId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(trackingId);
    setCopied(trackingId);
    setTimeout(() => setCopied(null), 2000);
  }, []);

  const getColumns = () => {
    return [
      { header: "Tracking #", key: "tracking" },
      { header: "ID", key: "id" },
      { header: "Estado", key: "status" },
      { header: "Sucursal", key: "branch" },
      { header: "Motorista", key: "driver" },
      { header: "Fecha", key: "date" },
      { header: "Total", key: "total" },
      { header: "Pagado", key: "paid" },
      { header: "Acciones", key: "actions" },
    ];
  };

  if (loading && orders.length === 0) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12 text-red-600">
        <p>Error: {error}</p>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <Search className="h-12 w-12 mb-4 opacity-50 hidden sm:block" />
        <p className="text-lg font-medium">No se encontraron órdenes</p>
        <p className="text-sm">Intenta ajustar los filtros de búsqueda</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table - >= 1280px */}
      <div className="hidden xl:block">
        <OrdersTable
          columns={getColumns()}
          emptyMessage="No hay órdenes históricas"
        >
          {orders.map((order) => (
            <tr
              key={order.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => onOrderClick(order.id.toString())}
            >
              {/* Tracking # Column */}
              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                <div className="flex items-center gap-2">
                  <span>{order.trackingId}</span>
                  <button
                    onClick={(e) => handleCopy(order.trackingId, e)}
                    className="p-1 hover:bg-gray-200 rounded transition-colors hidden sm:block"
                    title="Copiar ID"
                  >
                    {copied === order.trackingId ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                    )}
                  </button>
                </div>
              </td>

              {/* ID Column */}
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                #{order.id}
              </td>

              {/* Status Column */}
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <StatusBadge status={order.status} type="order" />
              </td>

              {/* Branch Column */}
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {order.branchName}
              </td>

              {/* Driver Column */}
              <td
                className={`max-w-40 truncate px-3 py-4 text-sm ${
                  order.driverName === "Sin asignar"
                    ? "text-gray-400"
                    : "text-gray-500"
                }`}
                title={order.driverName}
              >
                {order.driverName}
              </td>

              {/* Date Column */}
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {order.formattedDate}
              </td>

              {/* Total Column */}
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatCurrency(order.total, order.currency)}
              </td>

              {/* Paid Column */}
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    order.isPaid
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {order.isPaid ? "Pagado" : "Pendiente"}
                </span>
              </td>

              {/* Actions Column */}
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOrderClick(order.id.toString());
                  }}
                  className="text-indigo-600 hover:text-indigo-700"
                >
                  Ver detalles
                </Button>
              </td>
            </tr>
          ))}
        </OrdersTable>
      </div>

      {/* Mobile Cards - < 1280px */}
      <div className="block xl:hidden">
        <div className="flex flex-col gap-4">
          {orders.map((order) => (
            <Card
              key={order.id}
              className="bg-white rounded-lg shadow border p-3 sm:p-4 cursor-pointer hover:shadow-md transition-shadow w-full"
              onClick={() => onOrderClick(order.id.toString())}
              style={{
                minWidth: 0,
                wordBreak: "break-word",
                boxSizing: "border-box",
              }}
            >
              {/* Header with Tracking # and Status */}
              <div className="flex flex-col xs:flex-row xs:items-start xs:justify-between mb-3 gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                    {order.trackingId}
                  </h3>
                  <button
                    onClick={(e) => handleCopy(order.trackingId, e)}
                    className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0 hidden sm:block"
                    title="Copiar ID"
                  >
                    {copied === order.trackingId ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                    )}
                  </button>
                </div>
                <div className="flex-shrink-0 mt-2 xs:mt-0">
                  <StatusBadge status={order.status} type="order" />
                </div>
              </div>

              {/* ID and Date */}
              <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between mb-3 gap-1 xs:gap-2">
                <span className="text-xs sm:text-sm text-gray-600">
                  ID #{order.id}
                </span>
                <span className="text-xs sm:text-sm text-gray-600">
                  {order.formattedDate}
                </span>
              </div>

              {/* Branch and Total */}
              <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between mb-3 gap-1 xs:gap-2">
                <span className="text-xs sm:text-sm text-gray-600 truncate">
                  {order.branchName}
                </span>
                <span className="font-medium text-gray-900 flex-shrink-0">
                  {formatCurrency(order.total, order.currency)}
                </span>
              </div>

              {/* Driver */}
              <div className="mb-3 text-xs sm:text-sm">
                <span className="text-gray-600">Motorista: </span>
                <span
                  className={
                    order.driverName === "Sin asignar"
                      ? "text-gray-400"
                      : "text-gray-900"
                  }
                >
                  {order.driverName}
                </span>
              </div>

              {/* Payment Status */}
              <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-1 xs:gap-2">
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    order.isPaid
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {order.isPaid ? "Pagado" : "Pendiente"}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOrderClick(order.id.toString());
                  }}
                  className="text-indigo-600 hover:text-indigo-700 px-0"
                >
                  Ver detalles
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="flex justify-center p-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onLoadMore}
            disabled={loading}
            className="flex items-center gap-2"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin hidden sm:block" />
                Cargando...
              </>
            ) : (
              "Cargar más"
            )}
          </Button>
        </div>
      )}
    </>
  );
}

// Helper Components
function FiltersSection({
  filters,
  options,
  loadingOptions,
  optionsError,
  brands,
  loadingBrands,
  isSearchPending,
  onFilterChange,
  hasActiveFilters,
  onClearAllFilters,
}: {
  filters: FilterState;
  options: HistoryFilterOptions;
  loadingOptions: boolean;
  optionsError: string | null;
  brands: any[];
  loadingBrands: boolean;
  isSearchPending: boolean;
  onFilterChange: (key: keyof FilterState, value: string) => void;
  hasActiveFilters: boolean;
  onClearAllFilters: () => void;
}) {
  // The API accepts 90 calendar dates inclusively, so the earliest date is today - 89 days.
  const minimumStartDate = subDays(startOfDay(new Date()), 89);

  return (
    <div className="pt-4 sm:pt-5 space-y-3 sm:space-y-4">
      {/* Search Bar and Clear Filters - Responsive layout */}
      <div className="w-full">
        <label
          htmlFor="search"
          className="block text-sm font-medium text-gray-700 mb-1 sm:hidden"
        >
          Buscar
        </label>
        <div className="flex gap-2 sm:gap-3">
          <div className="flex-1">
            <Input
              id="search"
              type="text"
              placeholder="Buscar por tracking ID..."
              value={filters.searchTerm}
              onChange={(e) => onFilterChange("searchTerm", e.target.value)}
              icon={
                isSearchPending ? (
                  <RefreshCw className="w-4 h-4 animate-spin text-indigo-500 hidden sm:block" />
                ) : (
                  <Search className="w-4 h-4 hidden sm:block" />
                )
              }
              className="w-full min-w-0 text-ellipsis"
              style={{
                minWidth: 0,
                width: "100%",
                maxWidth: "100%",
              }}
            />
          </div>
          {hasActiveFilters && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClearAllFilters}
              className="h-10 rounded-lg hover:bg-red-50 border-gray-300 text-red-700 hover:text-red-800 flex items-center gap-1 sm:gap-2 flex-shrink-0"
              title="Limpiar todos los filtros"
            >
              <X className="w-4 h-4" />
              <span className="hidden sm:inline text-sm">Limpiar</span>
            </Button>
          )}
        </div>
        {isSearchPending && (
          <p className="text-xs text-indigo-600 mt-1">Buscando...</p>
        )}
      </div>

      {/* Filters Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Date Range Group */}

        {/* Desde Fecha */}
        <div>
          <label
            htmlFor="start-date-picker"
            className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2"
          >
            Desde
          </label>
          <DatePicker
            id="start-date-picker"
            value={filters.startDate}
            onChange={(value: string) => onFilterChange("startDate", value)}
            placeholder="Desde fecha"
            minDate={minimumStartDate}
            maxDate={new Date()} // No permite fechas futuras
            className="w-full"
          />
        </div>

        {/* Hasta Fecha */}
        <div>
          <label
            htmlFor="end-date-picker"
            className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2"
          >
            Hasta
          </label>
          <DatePicker
            id="end-date-picker"
            value={filters.endDate}
            onChange={(value: string) => onFilterChange("endDate", value)}
            placeholder="Hasta fecha"
            minDate={
              filters.startDate
                ? new Date(filters.startDate + "T00:00:00")
                : undefined
            } // No permite fechas anteriores a "desde"
            maxDate={new Date()} // No permite fechas futuras
            className="w-full"
          />
        </div>

        {/* Brand Select */}
        <div>
          <label
            htmlFor="brand-select"
            className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2"
          >
            Marca
          </label>
          <BrandSelect
            value={filters.selectedBrand}
            brands={brands}
            loading={loadingBrands}
            onValueChange={(value) => onFilterChange("selectedBrand", value)}
          />
        </div>

        {/* Status Select */}
        <div>
          <label
            htmlFor="status-select"
            className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2"
          >
            Estado
          </label>
          <StatusSelect
            value={filters.selectedStatus}
            onValueChange={(value) => onFilterChange("selectedStatus", value)}
          />
        </div>
      </div>

      {/* Active Filters Summary - Show on mobile for better UX */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <HistoryMultiSelect
          label="Sucursales"
          options={options.branches.filter(
            (branch) =>
              filters.selectedBrand === "all" ||
              branch.brandId === Number(filters.selectedBrand),
          )}
          value={filters.branchIds}
          onChange={(value) => onFilterChange("branchIds", value)}
          loading={loadingOptions}
        />
        <HistoryMultiSelect
          label="Motoristas"
          options={options.drivers}
          value={filters.driverIds}
          onChange={(value) => onFilterChange("driverIds", value)}
          loading={loadingOptions}
        />
      </div>
      {optionsError && (
        <p role="alert" className="text-sm text-red-600">
          {optionsError}
        </p>
      )}
      <ActiveFiltersSummary filters={filters} brands={brands} />
    </div>
  );
}

// New component to show active filters summary on mobile
function ActiveFiltersSummary({
  filters,
  brands,
}: {
  filters: FilterState;
  brands: any[];
}) {
  const activeFilters = [];

  if (filters.searchTerm) {
    activeFilters.push(`Búsqueda: "${filters.searchTerm}"`);
  }

  if (filters.selectedBrand !== "all") {
    const brandName =
      brands.find((b) => b.value.toString() === filters.selectedBrand)?.label ||
      "Marca seleccionada";
    activeFilters.push(`Marca: ${brandName}`);
  }

  if (filters.selectedStatus !== "all") {
    const statusOption = STATUS_OPTIONS.find(
      (s) => s.value === filters.selectedStatus,
    );
    activeFilters.push(
      `Estado: ${statusOption?.label || filters.selectedStatus}`,
    );
  }

  if (filters.startDate && filters.endDate) {
    const startFormatted = format(new Date(filters.startDate), "dd/MM/yyyy", {
      locale: es,
    });
    const endFormatted = format(new Date(filters.endDate), "dd/MM/yyyy", {
      locale: es,
    });
    activeFilters.push(`Fechas: ${startFormatted} - ${endFormatted}`);
  }

  if (activeFilters.length === 0) return null;

  return (
    <div className="block sm:hidden">
      <div className="text-xs text-gray-600 space-y-1">
        <div className="font-medium">Filtros activos:</div>
        {activeFilters.map((filter, index) => (
          <div key={index} className="bg-gray-100 px-2 py-1 rounded text-xs">
            {filter}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusSelect({
  value,
  onValueChange,
}: {
  value: string;
  onValueChange: (value: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger id="status-select" className="w-full">
        <SelectValue placeholder="Seleccionar estado" />
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function BrandSelect({
  value,
  brands,
  loading,
  onValueChange,
}: {
  value: string;
  brands: any[];
  loading: boolean;
  onValueChange: (value: string) => void;
}) {
  if (loading) {
    return <Skeleton className="h-10 w-full" />;
  }
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger id="brand-select" className="w-full">
        <SelectValue placeholder="Seleccionar marca" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem key="all" value="all">
          Todas las marcas
        </SelectItem>
        {brands.map((brand, idx) => (
          <SelectItem
            key={brand?.id ?? brand?.value ?? idx}
            value={brand?.value?.toString()}
          >
            {brand?.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Helper Functions
export function getStatusConfig(status: string) {
  switch (status) {
    case "completed":
      return {
        display: "Completado",
        color: "bg-green-100 text-green-700 border-green-300",
      };
    case "pending":
      return {
        display: "Pendiente",
        color: "bg-yellow-100 text-yellow-700 border-yellow-300",
      };
    case "sorted":
      return {
        display: "Surtida",
        color: "bg-orange-100 text-orange-700 border-orange-300",
      };
    case "cancelled":
      return {
        display: "Cancelado",
        color: "bg-red-100 text-red-700 border-red-300",
      };
    default:
      return {
        display: status,
        color: "bg-gray-100 text-gray-700 border-gray-300",
      };
  }
}
